import React, { useMemo } from "react";
import { DocumentTest } from "../../documentTests/documentTestsApi";

type Lang = "pl" | "en";

const T = (lang: Lang) =>
    lang === "pl"
        ? {
            lp: "Lp.",
            name: "Nazwa testu",
            planned: "Planowany termin",
            tester: "Tester (planowany)",
            approver: "Zatwierdzający (planowany)",
            none: "—",
            noTests: "Brak testów do wyświetlenia.",
        }
        : {
            lp: "No.",
            name: "Test name",
            planned: "Planned window",
            tester: "Tester (planned)",
            approver: "Approver (planned)",
            none: "—",
            noTests: "No tests to display.",
        };

// ---- helpers (proste formatowanie) ----
function fmtDateOnly(d?: string | null): string {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}
function fmtPlanned(a?: string | null, b?: string | null): string {
    if (!a && !b) return "";
    if (a && !b) return fmtDateOnly(a);
    if (!a && b) return fmtDateOnly(b);
    return `${fmtDateOnly(a!)} – ${fmtDateOnly(b!)}`;
}
function pickRole(test: DocumentTest, role: "TESTER" | "APPROVER") {
    const found = (test.assignments ?? []).find((x) => x.role === role);
    const name = found?.name?.trim();
    const email = found?.email?.trim();
    if (name && email) return `${name} <${email}>`;
    if (name) return name;
    if (email) return email;
    return "";
}

export interface PrintReadyScheduleTableProps {
    tests: DocumentTest[];
    lang?: Lang;
}

export const PrintPreviewDocumentScheduleTestView: React.FC<PrintReadyScheduleTableProps> = ({
    tests,
    lang = "pl",
}) => {
    const t = T(lang);

    const rows = useMemo(() => {
        const copy = [...(tests ?? [])];
        copy.sort((a, b) => {
            const ao = a.order ?? 0;
            const bo = b.order ?? 0;
            if (ao !== bo) return ao - bo;
            const an = a.testSpecification?.name ?? "";
            const bn = b.testSpecification?.name ?? "";
            return an.localeCompare(bn);
        });
        return copy;
    }, [tests]);

    // proste style inline do druku
    const tableStyle: React.CSSProperties = {
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: "16px",
    };
    const thStyle: React.CSSProperties = {
        border: "1px solid #000",
        padding: "6px 8px",
        background: "#eaeaea",
        fontWeight: 700,
        textAlign: "left",
        fontSize: "0.95rem",
    };
    const tdStyle: React.CSSProperties = {
        border: "1px solid #000",
        padding: "6px 8px",
        verticalAlign: "top",
        fontSize: "0.95rem",
        wordBreak: "break-word",
    };

    if (!rows.length) {
        return <div style={{ padding: 8 }}>{t.noTests}</div>;
    }

    return (
        <div style={{ background: "#fff", color: "#000", padding: 8 }}>
            <table style={tableStyle} aria-label="harmonogram-testow">
                <thead>
                    <tr>
                        <th style={{ ...thStyle, width: 60 }}>{t.lp}</th>
                        <th style={thStyle}>{t.name}</th>
                        <th style={{ ...thStyle, width: 220 }}>{t.planned}</th>
                        <th style={{ ...thStyle, width: 260 }}>{t.tester}</th>
                        <th style={{ ...thStyle, width: 260 }}>{t.approver}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((test, idx) => {
                        const planned = fmtPlanned(test.plannedStart, test.plannedEnd) || t.none;
                        const tester = pickRole(test, "TESTER") || t.none;
                        const approver = pickRole(test, "APPROVER") || t.none;

                        return (
                            <tr key={test.id ?? idx}>
                                <td style={tdStyle}>{idx + 1}</td>
                                <td style={tdStyle}>{test.testSpecification?.name ?? t.none}</td>
                                <td style={tdStyle}>{planned}</td>
                                <td style={tdStyle}>{tester}</td>
                                <td style={tdStyle}>{approver}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default PrintPreviewDocumentScheduleTestView;
